/**
 * PersonalReports (iter 72k) — 8-section scrolling report for Personal
 * workspaces. Business workspaces continue to use /accounting.
 *
 * Every dollar figure stays in the currency the underlying transaction
 * was entered in. When both USD and NGN rails have data the section
 * renders TWO stacked panels (USD panel + NGN panel) — never mixed.
 *
 * Sections (in order):
 *   1. Net Worth Statement
 *   2. Cash Flow (Income vs Expense)
 *   3. Portfolio Performance & Allocation
 *   4. Flexible Category Budgeting
 *   5. Bank Account Reconciliation
 *   6. Bank Fee & Interest Tracker
 *   7. Loan Amortization & Debt Paydown  (uses new /liabilities amortization endpoint)
 *   8. Cash Drag & Excess Liquidity
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import DrillDownModal from "@/components/DrillDownModal";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RailSectionHeader } from "@/components/accounting/_shared";
import {
  TrendingUp, TrendingDown, Wallet, PiggyBank, Scale, Target,
  Landmark, Percent, HandCoins, Droplets, Info, Plus, Trash2,
  Loader2, Printer, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

const LEAF = "#3A8760";
const LEAF_DEEP = "#2F6B4A";

// Category-name patterns for auto-detecting bank fees + interest income
const FEE_PATTERNS = /(bank\s*fee|maintenance|overdraft|service\s*charge|atm\s*fee|nsf)/i;
const INTEREST_PATTERNS = /(interest|apy|savings\s*interest|dividend)/i;

// -------- Empty state helper --------
function EmptyHint({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center text-center py-8 px-6 rounded-xl border border-dashed border-[var(--divider)] bg-[var(--card)]">
      <Icon className="w-6 h-6 text-[var(--muted-text)] mb-2" />
      <div className="font-medium text-sm text-[var(--text)]">{title}</div>
      <div className="text-xs text-[var(--muted-text)] mt-1 max-w-md">{hint}</div>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

// -------- Drill affordance for aggregate cards (iter 72o) --------
// Small right-aligned "See details" link that navigates to a filtered
// in-app page. Kept minimal so it doesn't compete with card values.
function SeeMoreLink({ href, label = "See details", testId }) {
  return (
    <a href={href} data-testid={testId} className="no-print inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--brand)] mt-2">
      {label} <ArrowUpRight className="w-3 h-3" />
    </a>
  );
}

// -------- Section wrapper --------
function Section({ id, icon: Icon, title, subtitle, children }) {
  return (
    <section id={id} data-testid={`personal-report-${id}`} className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
             style={{ background: "#EEF7F2", color: LEAF_DEEP }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-[var(--text)] leading-snug">{title}</h2>
          <p className="text-xs text-[var(--muted-text)] mt-0.5 leading-relaxed">{subtitle}</p>
        </div>
      </div>
      <div className="pl-13">{children}</div>
    </section>
  );
}

// -------- 1. Net Worth Statement --------
function NetWorthStatement({ accounts, portfolio, liabilities }) {
  const rails = useMemo(() => {
    const byCcy = {};
    // iter 72s — derive per-currency cash directly from account balances
    // (banks + cash + wallets). Credit-card accounts move to Liabilities as
    // their absolute owed amount (a -$500 card balance is $500 debt).
    for (const a of accounts || []) {
      const c = a.currency || "USD";
      if (!byCcy[c]) byCcy[c] = { currency: c, cash: 0, investments: 0, liabilities: 0 };
      const bal = Number(a.balance || 0);
      if (a.type === "card") {
        // Negative card balance = money owed (add to liabilities as positive).
        if (bal < 0) byCcy[c].liabilities += -bal;
        else byCcy[c].cash += bal; // paid-ahead card balance is a small asset
      } else {
        byCcy[c].cash += bal;
      }
    }
    for (const p of portfolio?.by_currency || []) {
      const c = p.currency;
      if (!byCcy[c]) byCcy[c] = { currency: c, cash: 0, investments: 0, liabilities: 0 };
      byCcy[c].investments += Number(p.value || 0);
    }
    for (const l of liabilities || []) {
      const c = l.currency || "USD";
      if (!byCcy[c]) byCcy[c] = { currency: c, cash: 0, investments: 0, liabilities: 0 };
      byCcy[c].liabilities += Number(l.balance || 0);
    }
    return Object.values(byCcy).map((r) => ({
      ...r,
      assets: r.cash + r.investments,
      net_worth: r.cash + r.investments - r.liabilities,
    })).sort((a, b) => (a.currency === "USD" ? -1 : b.currency === "USD" ? 1 : 0));
  }, [accounts, portfolio, liabilities]);

  if (!rails.length) {
    return <EmptyHint icon={Scale} title="No balance sheet data yet"
                       hint="Add a bank account or record a transaction — your Net Worth Statement will appear here per currency." />;
  }
  return (
    <div className="space-y-4">
      {rails.map((r) => (
        <div key={r.currency}>
          <RailSectionHeader currency={r.currency} subtitle="Assets − Liabilities = Net Worth"
                              testId={`nw-header-${r.currency}`} />
          <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
              <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">Cash & Wallets</div>
              <div className="font-mono-data text-xl mt-1">{formatCurrency(r.cash, r.currency)}</div>
              <SeeMoreLink href="/banks" label="Open banking" testId={`nw-drill-cash-${r.currency}`} />
            </div>
            <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
              <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">Investments</div>
              <div className="font-mono-data text-xl mt-1">{formatCurrency(r.investments, r.currency)}</div>
              <SeeMoreLink href="/investments" label="Open positions" testId={`nw-drill-invest-${r.currency}`} />
            </div>
            <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
              <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">Liabilities</div>
              <div className="font-mono-data text-xl mt-1 text-[#B4283A]">{formatCurrency(r.liabilities, r.currency)}</div>
              <SeeMoreLink href="/liabilities" label="Open liabilities" testId={`nw-drill-liab-${r.currency}`} />
            </div>
            <div className="rounded-xl border-2 border-[var(--brand)] p-4"
                 style={{ background: "#EEF7F2" }}>
              <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: LEAF_DEEP }}>Net Worth</div>
              <div className="font-mono-data text-2xl mt-1 font-semibold"
                   data-testid={`nw-total-${r.currency}`}
                   style={{ color: r.net_worth >= 0 ? LEAF_DEEP : "#B4283A" }}>
                {formatCurrency(r.net_worth, r.currency)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// -------- 2. Cash Flow --------
function CashFlow({ summary }) {
  const rails = summary?.by_currency || [];
  if (!rails.length) {
    return <EmptyHint icon={TrendingUp} title="No income/expense yet"
                       hint="Record some transactions this month — you'll see your inflows vs outflows here." />;
  }
  return (
    <div className="space-y-4">
      {rails.map((r) => {
        const inc = Number(r.income || 0), exp = Number(r.expense || 0);
        const net = inc - exp;
        const savingsRate = inc > 0 ? Math.max(0, Math.round((net / inc) * 100)) : 0;
        return (
          <div key={r.currency}>
            <RailSectionHeader currency={r.currency}
              subtitle={`Savings rate ${savingsRate}%`}
              testId={`cf-header-${r.currency}`}
              right={<div className={`font-mono-data text-sm ${net >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                Net {formatCurrency(net, r.currency)}
              </div>} />
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
                <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)] flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3 text-[#4C956C]" /> Income
                </div>
                <div className="font-mono-data text-xl mt-1 text-[#4C956C]"
                     data-testid={`cf-income-${r.currency}`}>
                  {formatCurrency(inc, r.currency)}
                </div>
                <SeeMoreLink href="/transactions?type=income" label="See income" testId={`cf-drill-income-${r.currency}`} />
              </div>
              <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
                <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)] flex items-center gap-1.5">
                  <TrendingDown className="w-3 h-3 text-[#E63946]" /> Expenses
                </div>
                <div className="font-mono-data text-xl mt-1 text-[#E63946]"
                     data-testid={`cf-expense-${r.currency}`}>
                  {formatCurrency(exp, r.currency)}
                </div>
                <SeeMoreLink href="/transactions?type=expense" label="See expenses" testId={`cf-drill-expense-${r.currency}`} />
              </div>
              <div className="rounded-xl border border-[var(--divider)] p-4"
                   style={{ background: net >= 0 ? "#EEF7F2" : "#FCE7E9" }}>
                <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">Kept</div>
                <div className="font-mono-data text-xl mt-1 font-semibold"
                     style={{ color: net >= 0 ? LEAF_DEEP : "#B4283A" }}>
                  {formatCurrency(Math.max(0, net), r.currency)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// -------- 3. Portfolio Performance & Allocation --------
function Portfolio({ portfolio }) {
  const rails = portfolio?.by_currency || [];
  const positions = portfolio?.positions || [];
  if (!rails.length) {
    return <EmptyHint icon={PiggyBank} title="No investments recorded"
                       hint="Head to Investments to add a stock, crypto, or money-market position."
                       action={<Button asChild size="sm" variant="outline"><a href="/investments">Open Investments →</a></Button>} />;
  }
  return (
    <div className="space-y-4">
      {rails.map((r) => {
        const positionsForRail = positions.filter((p) => (p.currency || "USD") === r.currency);
        const gain = Number(r.gain || (r.value - r.cost) || 0);
        const gainPct = r.cost > 0 ? (gain / r.cost) * 100 : 0;
        return (
          <div key={r.currency}>
            <RailSectionHeader currency={r.currency}
              subtitle="Positions in this rail"
              testId={`port-header-${r.currency}`}
              right={<div className={`font-mono-data text-sm ${gain >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                {gain >= 0 ? "+" : ""}{formatCurrency(gain, r.currency)} ({gainPct >= 0 ? "+" : ""}{gainPct.toFixed(1)}%)
              </div>} />
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
                <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">Market value</div>
                <div className="font-mono-data text-xl mt-1">{formatCurrency(r.value, r.currency)}</div>
                <SeeMoreLink href="/investments" label="Open positions" testId={`port-drill-${r.currency}`} />
              </div>
              <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
                <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">Cost basis</div>
                <div className="font-mono-data text-xl mt-1">{formatCurrency(r.cost, r.currency)}</div>
              </div>
              <div className="rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)]">
                <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">Positions</div>
                <div className="font-mono-data text-xl mt-1">{positionsForRail.length}</div>
              </div>
            </div>
            {positionsForRail.length > 0 && (
              <table className="w-full text-sm mt-3 border border-[var(--divider)] rounded-lg overflow-hidden">
                <thead className="bg-[var(--hover)]">
                  <tr className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] text-left">
                    <th className="px-4 py-2">Symbol</th>
                    <th className="px-4 py-2">Kind</th>
                    <th className="px-4 py-2 text-right">Qty</th>
                    <th className="px-4 py-2 text-right">Value</th>
                    <th className="px-4 py-2 text-right">Gain</th>
                  </tr>
                </thead>
                <tbody>
                  {positionsForRail.map((p) => {
                    const pGain = Number(p.market_value || 0) - Number(p.cost_basis || 0);
                    return (
                      <tr key={p.id || p.symbol} className="border-t border-[var(--divider)]">
                        <td className="px-4 py-2 font-mono-data">{p.symbol || p.name || "—"}</td>
                        <td className="px-4 py-2 text-xs text-[var(--muted-text)] uppercase">{p.kind || "asset"}</td>
                        <td className="px-4 py-2 text-right font-mono-data">{Number(p.quantity || 0).toLocaleString()}</td>
                        <td className="px-4 py-2 text-right font-mono-data">{formatCurrency(p.market_value, r.currency)}</td>
                        <td className={`px-4 py-2 text-right font-mono-data ${pGain >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                          {pGain >= 0 ? "+" : ""}{formatCurrency(pGain, r.currency)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

// -------- 4. Flexible Category Budgeting --------
function Budgeting({ budgets, transactions, onDrill }) {
  const monthStart = useMemo(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const spent = useMemo(() => {
    const byCat = {};
    for (const t of transactions || []) {
      if (t.type !== "expense") continue;
      const d = new Date(t.date);
      if (d < monthStart) continue;
      const k = `${t.category}::${t.currency || "USD"}`;
      byCat[k] = (byCat[k] || 0) + Number(t.amount || 0);
    }
    return byCat;
  }, [transactions, monthStart]);

  const railed = useMemo(() => {
    const byCcy = {};
    for (const b of budgets || []) {
      const c = b.currency || "USD";
      const key = `${b.category}::${c}`;
      const used = spent[key] || 0;
      const cap = Number(b.amount || 0);
      const pct = cap > 0 ? (used / cap) * 100 : 0;
      (byCcy[c] = byCcy[c] || []).push({ ...b, used, cap, pct });
    }
    return byCcy;
  }, [budgets, spent]);

  if (!budgets?.length) {
    return <EmptyHint icon={Target} title="No budgets yet"
                       hint="Set a ceiling on dining, software, utilities and other categories."
                       action={<Button asChild size="sm" variant="outline"><a href="/budgets">Open Budgets →</a></Button>} />;
  }
  return (
    <div className="space-y-4">
      {Object.entries(railed).map(([ccy, rows]) => (
        <div key={ccy}>
          <RailSectionHeader currency={ccy} subtitle="Month-to-date spending vs ceilings"
                              testId={`budget-header-${ccy}`} />
          <div className="mt-2 space-y-2">
            {rows.map((b) => (
              <div key={`${b.category}-${ccy}`}
                   className={`rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)] ${onDrill ? "cursor-pointer hover:border-[var(--brand)]" : ""}`}
                   data-testid={`budget-row-${b.category}-${ccy}`}
                   title={onDrill ? "Click to see the expenses under this budget" : undefined}
                   onClick={onDrill ? () => {
                     const monthStart = new Date();
                     monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
                     const iso = monthStart.toISOString().slice(0, 10);
                     onDrill(`${b.category} · ${formatCurrency(b.used, ccy)} spent this month`, {
                       kind: "expense", category: b.category, start: iso,
                     }, ccy);
                   } : undefined}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="font-medium text-sm flex items-center gap-1.5">
                    {b.category}
                    {onDrill && <ArrowUpRight className="w-3 h-3 text-[var(--muted-text)]" />}
                  </div>
                  <div className="text-xs text-[var(--muted-text)]">
                    <span className="font-mono-data">{formatCurrency(b.used, ccy)}</span>
                    <span> of </span>
                    <span className="font-mono-data">{formatCurrency(b.cap, ccy)}</span>
                    <span className={`ml-2 font-semibold ${b.pct >= 100 ? "text-[#B4283A]" : b.pct >= 80 ? "text-[#D97706]" : "text-[#2F6B4A]"}`}>
                      {Math.round(b.pct)}%
                    </span>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-[var(--hover)] overflow-hidden mt-2">
                  <div className="h-full transition-all"
                       style={{
                         width: `${Math.min(100, b.pct)}%`,
                         background: b.pct >= 100 ? "#B4283A" : b.pct >= 80 ? "#D97706" : LEAF,
                       }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// -------- 5. Bank Account Reconciliation --------
function BankRecon({ accounts, transactions, onDrill }) {
  const rails = useMemo(() => {
    const byCcy = {};
    for (const a of accounts || []) {
      if (a.type !== "bank") continue;
      const c = a.currency || "USD";
      const stmtBalance = Number(a.statement_balance || 0);
      const bookBalance = Number(a.balance || 0);
      const diff = bookBalance - stmtBalance;
      (byCcy[c] = byCcy[c] || []).push({
        ...a,
        book: bookBalance,
        statement: stmtBalance,
        difference: diff,
        has_statement: a.statement_balance != null,
      });
    }
    return byCcy;
  }, [accounts]);

  if (!Object.keys(rails).length) {
    return <EmptyHint icon={Landmark} title="No bank accounts to reconcile"
                       hint="Add a bank account under Accounts, then paste in the statement balance to compare against the app's number." />;
  }
  return (
    <div className="space-y-4">
      {Object.entries(rails).map(([ccy, rows]) => (
        <div key={ccy}>
          <RailSectionHeader currency={ccy} subtitle="App balance vs statement balance"
                              testId={`recon-header-${ccy}`} />
          <div className="mt-2 space-y-2">
            {rows.map((a) => (
              <div key={a.id}
                   className={`rounded-xl border border-[var(--divider)] p-4 bg-[var(--card)] ${onDrill ? "cursor-pointer hover:border-[var(--brand)]" : ""}`}
                   data-testid={`recon-row-${a.id}`}
                   title={onDrill ? "Click to see this account's transactions" : undefined}
                   onClick={onDrill ? () => onDrill(
                     `${a.name} · ${formatCurrency(a.book, ccy)} book balance`,
                     { kind: "wallet", account_id: a.id },
                     ccy,
                   ) : undefined}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-1.5">
                      {a.name}
                      {onDrill && <ArrowUpRight className="w-3 h-3 text-[var(--muted-text)]" />}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mt-0.5">
                      {a.institution_name || "Bank"} · {ccy}
                    </div>
                  </div>
                  <div className="text-xs grid grid-cols-3 gap-4 min-w-fit">
                    <div>
                      <div className="text-[10px] uppercase text-[var(--muted-text)]">App</div>
                      <div className="font-mono-data">{formatCurrency(a.book, ccy)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-[var(--muted-text)]">Statement</div>
                      <div className="font-mono-data">{a.has_statement ? formatCurrency(a.statement, ccy) : "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-[var(--muted-text)]">Diff</div>
                      <div className={`font-mono-data font-semibold ${
                        Math.abs(a.difference) < 0.01 ? "text-[#2F6B4A]" : "text-[#B4283A]"
                      }`}>
                        {a.has_statement
                          ? `${a.difference >= 0 ? "+" : ""}${formatCurrency(a.difference, ccy)}`
                          : "n/a"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="text-xs text-[var(--muted-text)] flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Add your latest statement balance in Accounts to catch duplicate sync errors or pending holds before they mess up your budget.
      </div>
    </div>
  );
}

// -------- 6. Bank Fee & Interest Tracker --------
function FeesAndInterest({ transactions, onDrill }) {
  const { monthStart, yearStart } = useMemo(() => {
    const m = new Date(); m.setDate(1); m.setHours(0, 0, 0, 0);
    const y = new Date(); y.setMonth(0, 1); y.setHours(0, 0, 0, 0);
    return { monthStart: m, yearStart: y };
  }, []);

  const grouped = useMemo(() => {
    const byCcy = {};
    for (const t of transactions || []) {
      const cat = (t.category || "").trim();
      const isFee = t.type === "expense" && FEE_PATTERNS.test(cat);
      const isInterest = t.type === "income" && INTEREST_PATTERNS.test(cat);
      if (!isFee && !isInterest) continue;
      const c = t.currency || "USD";
      const b = byCcy[c] = byCcy[c] || {
        fees_month: 0, fees_year: 0, fees_all: 0,
        interest_month: 0, interest_year: 0, interest_all: 0,
        rows: [],
      };
      const d = new Date(t.date);
      const amt = Number(t.amount || 0);
      if (isFee) {
        b.fees_all += amt;
        if (d >= yearStart) b.fees_year += amt;
        if (d >= monthStart) b.fees_month += amt;
      } else {
        b.interest_all += amt;
        if (d >= yearStart) b.interest_year += amt;
        if (d >= monthStart) b.interest_month += amt;
      }
      b.rows.push({ date: t.date, kind: isFee ? "fee" : "interest", amount: amt, category: cat, note: t.note });
    }
    for (const c in byCcy) byCcy[c].rows.sort((a, b) => (b.date > a.date ? 1 : -1));
    return byCcy;
  }, [transactions, monthStart, yearStart]);

  if (!Object.keys(grouped).length) {
    return <EmptyHint icon={Percent} title="No fees or interest detected"
                       hint={"Tag transactions with categories like \"Bank Fee\", \"Overdraft\", \"Interest\" or \"APY\" — this tracker auto-detects them."} />;
  }
  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([ccy, b]) => (
        <div key={ccy}>
          <RailSectionHeader currency={ccy}
            subtitle={`Net ${formatCurrency(b.interest_all - b.fees_all, ccy)} to date`}
            testId={`fee-header-${ccy}`}
            right={<div className={`font-mono-data text-sm ${b.interest_all >= b.fees_all ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
              {(b.interest_all - b.fees_all) >= 0 ? "You're ahead" : "Bank's ahead"}
            </div>} />
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Fees · MTD" value={formatCurrency(b.fees_month, ccy)} tone="neg" />
            <MiniStat label="Fees · YTD" value={formatCurrency(b.fees_year, ccy)} tone="neg" />
            <MiniStat label="Interest · MTD" value={formatCurrency(b.interest_month, ccy)} tone="pos" />
            <MiniStat label="Interest · YTD" value={formatCurrency(b.interest_year, ccy)} tone="pos" />
          </div>
          {b.rows.length > 0 && (
            <table className="w-full text-sm mt-3 border border-[var(--divider)] rounded-lg overflow-hidden">
              <thead className="bg-[var(--hover)]">
                <tr className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] text-left">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Kind</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {b.rows.slice(0, 10).map((r, i) => (
                  <tr key={`${r.date}-${r.category}-${r.amount}-${i}`}
                      className={`border-t border-[var(--divider)] ${onDrill ? "cursor-pointer hover:bg-[var(--hover)]" : ""}`}
                      title={onDrill ? "Click to see all rows of this category" : undefined}
                      onClick={onDrill ? () => onDrill(
                        `${r.category} · ${r.kind === "fee" ? "expense" : "income"} drill`,
                        { kind: r.kind === "fee" ? "expense" : "income", category: r.category },
                        ccy,
                      ) : undefined}>
                    <td className="px-4 py-2 font-mono-data text-xs">{formatDate(r.date)}</td>
                    <td className="px-4 py-2 text-[10px] uppercase tracking-widest"
                        style={{ color: r.kind === "fee" ? "#B4283A" : LEAF_DEEP }}>
                      {r.kind}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {r.category}
                      {onDrill && <ArrowUpRight className="w-3 h-3 inline-block ml-1 text-[var(--muted-text)]" />}
                    </td>
                    <td className={`px-4 py-2 text-right font-mono-data ${r.kind === "fee" ? "text-[#B4283A]" : "text-[#2F6B4A]"}`}>
                      {r.kind === "fee" ? "−" : "+"}{formatCurrency(r.amount, ccy)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

function MiniStat({ label, value, tone, href }) {
  const color = tone === "pos" ? "#2F6B4A" : tone === "neg" ? "#B4283A" : "var(--text)";
  return (
    <div className="rounded-xl border border-[var(--divider)] p-3 bg-[var(--card)]">
      <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">{label}</div>
      <div className="font-mono-data text-lg mt-1" style={{ color }}>{value}</div>
      {href && (
        <a href={href} className="no-print inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--brand)] mt-1">
          Open <ArrowUpRight className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

// -------- 7. Loan Amortization & Debt Paydown --------
function LoanAmortization({ liabilities, onCreated, onDeleted }) {
  const [selectedId, setSelectedId] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", kind: "loan", currency: "USD",
    original_principal: "", interest_rate: "",
    term_months: "", monthly_payment: "", start_date: "",
  });
  const [saving, setSaving] = useState(false);

  const loans = (liabilities || []).filter((l) => l.term_months && l.original_principal);

  const loadSchedule = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/liabilities/${id}/amortization`);
      setSchedule(data);
    } catch (e) {
      setSchedule(null);
      toast.error(e.response?.data?.detail || "Could not load schedule");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (loans.length && !selectedId) setSelectedId(loans[0].id);
  }, [loans, selectedId]);
  useEffect(() => { if (selectedId) loadSchedule(selectedId); }, [selectedId, loadSchedule]);

  const saveLoan = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!Number(form.original_principal)) return toast.error("Principal is required");
    if (!Number(form.term_months)) return toast.error("Term (in months) is required");
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        kind: form.kind,
        currency: form.currency,
        balance: Number(form.original_principal),
        original_principal: Number(form.original_principal),
        interest_rate: Number(form.interest_rate) || 0,
        term_months: parseInt(form.term_months, 10),
        monthly_payment: Number(form.monthly_payment) || null,
        start_date: form.start_date || null,
      };
      const { data } = await api.post("/liabilities", body);
      toast.success("Loan added");
      setForm({ name: "", kind: "loan", currency: "USD", original_principal: "",
                interest_rate: "", term_months: "", monthly_payment: "", start_date: "" });
      setAddOpen(false);
      onCreated && onCreated();
      setSelectedId(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const removeLoan = async (id) => {
    if (!window.confirm("Delete this loan? Amortization data will be lost.")) return;
    try {
      await api.delete(`/liabilities/${id}`);
      toast.success("Loan removed");
      if (id === selectedId) { setSelectedId(null); setSchedule(null); }
      onDeleted && onDeleted();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {loans.length > 0 && (
          <Select value={selectedId || ""} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="loan-picker" className="w-full sm:w-80">
              <SelectValue placeholder="Pick a loan" />
            </SelectTrigger>
            <SelectContent>
              {loans.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.name} · {l.currency} · {formatCurrency(l.original_principal || l.balance, l.currency)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button data-testid="loan-add-btn" variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add loan
        </Button>
        {selectedId && (
          <Button variant="ghost" size="sm" className="text-[#B4283A]"
                  data-testid="loan-remove-btn"
                  onClick={() => removeLoan(selectedId)}>
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        )}
      </div>

      {!loans.length ? (
        <EmptyHint icon={HandCoins} title="No loans tracked yet"
                    hint="Add a mortgage, car loan, or credit-line to see the principal-vs-interest split and how fast you're paying it down." />
      ) : loading ? (
        <div className="text-sm text-[var(--muted-text)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading schedule…</div>
      ) : schedule ? (
        <div>
          <RailSectionHeader currency={schedule.summary.currency}
            subtitle={`${schedule.summary.name} · ${schedule.summary.apr}% APR · ${schedule.summary.term_months} months`}
            testId={`loan-header-${schedule.summary.currency}`}
            right={<div className="text-xs">
              Total interest <span className="font-mono-data text-[#B4283A]">{formatCurrency(schedule.summary.total_interest, schedule.summary.currency)}</span>
            </div>} />
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Monthly payment" value={formatCurrency(schedule.summary.monthly_payment, schedule.summary.currency)} />
            <MiniStat label="Original principal" value={formatCurrency(schedule.summary.original_principal, schedule.summary.currency)} />
            <MiniStat label="Total paid" value={formatCurrency(schedule.summary.total_paid, schedule.summary.currency)} />
            <MiniStat label="Total interest" tone="neg" value={formatCurrency(schedule.summary.total_interest, schedule.summary.currency)} />
          </div>
          <div className="mt-3 max-h-80 overflow-auto border border-[var(--divider)] rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-[var(--hover)] sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] text-left">
                  <th className="px-4 py-2">Month</th>
                  <th className="px-4 py-2 text-right">Principal</th>
                  <th className="px-4 py-2 text-right">Interest</th>
                  <th className="px-4 py-2 text-right">Remaining balance</th>
                </tr>
              </thead>
              <tbody>
                {schedule.schedule.map((row) => (
                  <tr key={row.month} className="border-t border-[var(--divider)]">
                    <td className="px-4 py-2 font-mono-data text-xs">{row.month}</td>
                    <td className="px-4 py-2 text-right font-mono-data text-[#2F6B4A]">
                      {formatCurrency(row.principal, schedule.summary.currency)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono-data text-[#B4283A]">
                      {formatCurrency(row.interest, schedule.summary.currency)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono-data">
                      {formatCurrency(row.balance, schedule.summary.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Add loan dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="loan-add-dialog">
          <DialogHeader><DialogTitle>Add a loan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label>
                <Input data-testid="loan-form-name" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="loan">Personal loan</SelectItem>
                    <SelectItem value="mortgage">Mortgage</SelectItem>
                    <SelectItem value="credit_card">Credit card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="loan-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="NGN">NGN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>APR %</Label>
                <Input data-testid="loan-form-apr" type="number" step="0.01" value={form.interest_rate}
                       onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Original principal</Label>
                <Input data-testid="loan-form-principal" type="number" step="0.01" value={form.original_principal}
                       onChange={(e) => setForm({ ...form, original_principal: e.target.value })} /></div>
              <div><Label>Term (months)</Label>
                <Input data-testid="loan-form-term" type="number" step="1" value={form.term_months}
                       onChange={(e) => setForm({ ...form, term_months: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Monthly payment (optional)</Label>
                <Input type="number" step="0.01" value={form.monthly_payment}
                       onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })}
                       placeholder="Auto-calculated" /></div>
              <div><Label>Start date</Label>
                <Input type="date" value={form.start_date}
                       onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="loan-form-save" onClick={saveLoan} disabled={saving}
                    className="rounded-full text-white" style={{ background: LEAF_DEEP }}>
              {saving ? "Saving…" : "Add loan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- 8. Cash Drag & Excess Liquidity --------
function CashDrag({ accounts, portfolio }) {
  const rails = useMemo(() => {
    const byCcy = {};
    for (const a of accounts || []) {
      const c = a.currency || "USD";
      const bal = Number(a.balance || 0);
      if (!byCcy[c]) byCcy[c] = { currency: c, checking: 0, hy: 0, invested: 0 };
      // Heuristic: bank/cash + name doesn't contain "savings"/"MMF" → checking
      const nameLc = (a.name || "").toLowerCase();
      if (nameLc.includes("savings") || nameLc.includes("mmf") || nameLc.includes("money market")) {
        byCcy[c].hy += bal;
      } else if (a.type === "cash" || a.type === "bank") {
        byCcy[c].checking += bal;
      } else {
        byCcy[c].hy += bal;
      }
    }
    for (const p of portfolio?.by_currency || []) {
      const c = p.currency;
      if (!byCcy[c]) byCcy[c] = { currency: c, checking: 0, hy: 0, invested: 0 };
      byCcy[c].invested += Number(p.value || 0);
    }
    return Object.values(byCcy).map((r) => {
      const total_liquid = r.checking + r.hy + r.invested;
      const drag_pct = total_liquid > 0 ? (r.checking / total_liquid) * 100 : 0;
      return { ...r, total_liquid, drag_pct };
    }).sort((a, b) => (a.currency === "USD" ? -1 : b.currency === "USD" ? 1 : 0));
  }, [accounts, portfolio]);

  if (!rails.length) {
    return <EmptyHint icon={Droplets} title="No liquidity to analyse yet"
                       hint="Once you've added at least one bank account and one investment, we'll show your idle-cash ratio." />;
  }
  return (
    <div className="space-y-4">
      {rails.map((r) => (
        <div key={r.currency}>
          <RailSectionHeader currency={r.currency}
            subtitle={`${Math.round(r.drag_pct)}% of assets sitting in low-yield checking`}
            testId={`drag-header-${r.currency}`}
            right={<div className={`text-xs font-semibold ${
              r.drag_pct > 30 ? "text-[#B4283A]" : r.drag_pct > 15 ? "text-[#D97706]" : "text-[#2F6B4A]"
            }`}>
              {r.drag_pct > 30 ? "High drag" : r.drag_pct > 15 ? "Moderate" : "Efficient"}
            </div>} />
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Checking (low-yield)" value={formatCurrency(r.checking, r.currency)} tone="neg" href="/banks" />
            <MiniStat label="Savings / MMF" value={formatCurrency(r.hy, r.currency)} href="/banks" />
            <MiniStat label="Invested" value={formatCurrency(r.invested, r.currency)} tone="pos" href="/investments" />
            <MiniStat label="Total liquid" value={formatCurrency(r.total_liquid, r.currency)} />
          </div>
          <div className="mt-3 h-3 rounded-full overflow-hidden bg-[var(--hover)] flex">
            <div style={{ width: `${r.total_liquid > 0 ? (r.checking / r.total_liquid) * 100 : 0}%`, background: "#B4283A" }} title="Checking" />
            <div style={{ width: `${r.total_liquid > 0 ? (r.hy / r.total_liquid) * 100 : 0}%`, background: "#D97706" }} title="Savings" />
            <div style={{ width: `${r.total_liquid > 0 ? (r.invested / r.total_liquid) * 100 : 0}%`, background: LEAF }} title="Invested" />
          </div>
          {r.drag_pct > 30 && (
            <div className="text-xs text-[var(--muted-text)] mt-2 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Consider sweeping some of that checking balance into a high-yield savings or money market to earn APY.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// -------- Page --------
export default function PersonalReports() {
  const [summary, setSummary] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter 72o: shared drill state — every drillable row across the 8
  // sections calls `openDrill(title, params, currency)`.
  const [drill, setDrill] = useState(null);
  const openDrill = useCallback((title, params, currency) => {
    setDrill({ title, params, currency: currency || "USD" });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const safe = (p) => p.then(({ data }) => data).catch(() => null);
    const [sum, port, accs, buds, txs, libs] = await Promise.all([
      safe(api.get("/analytics/summary")),
      safe(api.get("/investments/portfolio")),
      safe(api.get("/accounts")),
      safe(api.get("/budgets")),
      safe(api.get("/transactions", { params: { limit: 1000 } })),
      safe(api.get("/liabilities")),
    ]);
    setSummary(sum);
    setPortfolio(port);
    setAccounts(accs || []);
    setBudgets(buds || []);
    setTransactions(txs || []);
    setLiabilities(libs || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      {/* iter 72l — print stylesheet: hides app chrome + page-breaks per section
          so "Save as PDF" produces a clean 8-page-ish PDF. */}
      <style>{`
        @media print {
          /* Reset every colour to sensible print defaults & drop shadows. */
          * { box-shadow: none !important; }
          body { background: #ffffff !important; }
          /* Hide sidebars, top-bar, buttons, install prompts. */
          [data-testid="app-topbar"],
          aside,
          .no-print { display: none !important; }
          /* Give the main column the full page. */
          main, .flex-1 { padding: 0 !important; }
          .personal-reports-root { max-width: 100% !important; padding: 0 !important; }
          /* Keep each section on its own page group. */
          section[data-testid^="personal-report-"] {
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 24px;
          }
          /* Force colours to print (browsers strip them by default). */
          html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          /* Tables & tall lists shouldn't split awkwardly. */
          table { break-inside: auto; }
          tr { break-inside: avoid; break-after: auto; }
          /* Long loan schedules can page-break, but keep header rows visible. */
          thead { display: table-header-group; }
        }
      `}</style>
      <div className="max-w-6xl mx-auto space-y-10 pb-16 personal-reports-root">
        <header data-testid="personal-reports-hero" className="border-b border-[var(--divider)] pb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-[var(--muted-text)]" style={{ color: LEAF_DEEP }}>Personal Reports</div>
            <h1 className="font-display text-4xl mt-2">Your money, on record</h1>
            <p className="text-sm text-[var(--muted-text)] mt-2 max-w-2xl">
              Eight reports tailored to personal finance. Every figure stays in the currency it was entered — USD stays USD, NGN stays NGN, no cross-mixing.
            </p>
          </div>
          <Button
            data-testid="personal-reports-print"
            variant="outline"
            className="no-print h-11 rounded-full"
            onClick={() => {
              // Give the browser a beat to redraw before the print dialog opens.
              setTimeout(() => window.print(), 60);
            }}
            title="Opens your browser's print dialog — choose 'Save as PDF' as the destination"
          >
            <Printer className="w-4 h-4 mr-2" /> Save as PDF
          </Button>
        </header>
        {loading ? (
          <div className="text-sm text-[var(--muted-text)] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading reports…
          </div>
        ) : (
          <>
            <Section id="net-worth" icon={Scale} title="Net Worth Statement"
                     subtitle="Total Assets (Cash + Investments) minus Liabilities. Track your absolute wealth progression.">
              <NetWorthStatement accounts={accounts} portfolio={portfolio} liabilities={liabilities} />
            </Section>

            <Section id="cash-flow" icon={TrendingUp} title="Cash Flow · Income vs Expense"
                     subtitle="Month-to-date inflows vs outflows. A positive savings rate fuels future investments.">
              <CashFlow summary={summary} />
            </Section>

            <Section id="portfolio" icon={PiggyBank} title="Portfolio Performance & Allocation"
                     subtitle="Individual stock performance, dividend earnings, and money-market yields.">
              <Portfolio portfolio={portfolio} />
            </Section>

            <Section id="budgeting" icon={Target} title="Flexible Category Budgeting"
                     subtitle="Spending ceilings assigned to categories like dining, software, utilities.">
              <Budgeting budgets={budgets} transactions={transactions} onDrill={openDrill} />
            </Section>

            <Section id="bank-recon" icon={Landmark} title="Bank Account Reconciliation"
                     subtitle="Discrepancies between the app's balance and the true statement balance.">
              <BankRecon accounts={accounts} transactions={transactions} onDrill={openDrill} />
            </Section>

            <Section id="fees-interest" icon={Percent} title="Bank Fee & Interest Tracker"
                     subtitle="Accumulated fees, overdrafts, and interest earned. Are you paying the bank or is the bank paying you?">
              <FeesAndInterest transactions={transactions} onDrill={openDrill} />
            </Section>

            <Section id="loans" icon={HandCoins} title="Loan Amortization & Debt Paydown"
                     subtitle="Principal vs interest breakdown on mortgages, car loans, and credit lines.">
              <LoanAmortization liabilities={liabilities} onCreated={load} onDeleted={load} />
            </Section>

            <Section id="cash-drag" icon={Droplets} title="Cash Drag & Excess Liquidity"
                     subtitle="Uninvested low-yield cash vs high-yield / invested balances. Flags idle capital.">
              <CashDrag accounts={accounts} portfolio={portfolio} />
            </Section>
          </>
        )}
      </div>
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={drill?.currency || "USD"}
      />
    </AppShell>
  );
}
